import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["direction", "media"];
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { UpsellMixin } from '../../mixins/components/UpsellMixin';
import { Card } from '../Card/Card';
import { CloseButton } from '../CloseButton/CloseButton';
import { MediaObject } from '../MediaObject/MediaObject';
import { TextButton } from '../TextButton/TextButton';

/**
 * @extends Card
 * @related ['MediaObject']
 * @since 7.8.0
 */
var Upsell = /*#__PURE__*/styled(function (props) {
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    openState = _useState2[0],
    setOpenState = _useState2[1];
  if (
  // Controlled closed
  typeof props.isOpen === 'boolean' && !props.isOpen ||
  // Uncontrolled closed
  typeof props.isOpen !== 'boolean' && !openState) {
    return null;
  }
  var renderLayout = props.renderLayout;
  var content = renderLayout;
  if (typeof renderLayout === 'function') {
    content = renderLayout(Object.assign({}, props, {
      onClose: function onClose() {
        setOpenState(false);
        if (props.onClose) {
          props.onClose();
        }
      }
    }));
  }

  // Prevent prop bleed to DOM
  var direction = props.direction,
    media = props.media,
    rest = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/React.createElement(Card, rest, content);
}).withConfig({
  displayName: "Upsell",
  componentId: "c11n-8-111-1__sc-1tgvknj-0"
})(["", ""], UpsellMixin);
Upsell.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A `Button` node used as the primary call to action.
   */
  actionButton: PropTypes.node,
  /**
   * Customize the color of the upsell. When using a dark background, the text will be inverted.
   */
  background: PropTypes.oneOf(['backgroundWhite', 'backgroundBlue', 'backgroundDarkBlue', 'blue200']),
  /**
   * A `Text` node.
   */
  body: PropTypes.node,
  /**
   * By default, an upsell can be closed. Set this to `false` to disallow closing of the upsell.
   */
  closeable: PropTypes.bool,
  /**
   * @deprecated Depending on the layout, the close button may appear as an icon or a text button.
   * This will set the value when close is a text button.
   */
  closeText: PropTypes.string,
  /**
   * Should always be 'secondary' for Upsells.
   *
   * @ignore
   */
  cardType: PropTypes.oneOf(['secondary']),
  /**
   * The direction of the media to be used within the
   * `MediaObject` component.
   */
  direction: PropTypes.oneOf(['row', 'column']),
  /**
   * Inherited from `Card`.
   */
  cardElevation: PropTypes.oneOf([0, 1]),
  /**
   * A `Heading` node.
   */
  heading: PropTypes.node,
  /**
   * A closeable Upsell is uncontrolled by default.
   * You can specify `isOpen` to manually control the visibility of the component.
   */
  isOpen: PropTypes.bool,
  /**
   * An `Image` or `Icon` node.
   */
  media: PropTypes.node,
  /**
   * Function called when the upsell is closed.
   */
  onClose: PropTypes.func
} : {};
Upsell.defaultProps = {
  background: 'backgroundWhite',
  cardType: 'secondary',
  closeable: true,
  direction: 'row',
  cardElevation: 1,
  heading: null,
  isOpen: null,
  media: null,
  renderLayout: function renderLayout(_ref) {
    var actionButton = _ref.actionButton,
      body = _ref.body,
      closeable = _ref.closeable,
      direction = _ref.direction,
      heading = _ref.heading,
      media = _ref.media,
      onClose = _ref.onClose;
    var align = 'start';
    if (direction === 'column') {
      align = 'center';
    }
    var close;
    if (closeable) {
      close = /*#__PURE__*/React.createElement(CloseButton, {
        onClick: onClose
      });
    }
    var action;
    if (actionButton) {
      action = /*#__PURE__*/React.createElement(TextButton, _extends({}, actionButton.props, {
        buttonType: "primary"
      }));
    }
    return /*#__PURE__*/React.createElement(MediaObject, {
      media: media,
      direction: direction,
      align: align
    }, heading, body, action, close);
  }
};

/** @component */
export { Upsell };